import React from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import FormButton from '@ui/button/FormButton';
import Message from '@ui/message/Message';
import Loader from '@ui/loader/Loader';
import DataPlatform from '@app/model/_common/DataPlatform';
import DataProvider from '@app/model/_common/DataProvider';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { BookingPositionState } from './model/BookingPositionState';
import BookingPositionService from './service/BookingPositionService';
import { BookingPositionQueryKeys } from './service/BookingPositionQueryKeys';
import BookingRankingFilterBar, { BookingRankingFilterValues } from './BookingRankingFilterBar';
import PositionChartCard from './PositionChartCard';
import CompsetTable, { OtherCityTable } from './CompsetTable';
import NightGrid from './detail/NightGrid';
import MonthTable from './detail/MonthTable';
import CompsetDetailsTable from './detail/CompsetDetailsTable';
import PositionPriceScatter from './detail/PositionPriceScatter';
import { dayLabel, dayMonthLabel, insightText, nightsWord, withSearch } from './BookingRanking.utilities';

export const BOOKING_RANKING_PATH = '/secured/rateshopper/rates/booking-ranking';
export const BOOKING_RANKING_DETAIL_PATH = `${BOOKING_RANKING_PATH}/detail`;

type View = 'simple' | 'detail';

const HowToRead: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const scan = state.scan;
	const country = state.countries[0]?.name ?? 'the home country';
	return <p className="max-w-4xl text-xs leading-5 text-gray-600">
		Booking.com shows each guest a different order. Each day RoomPulse checks every 3rd night of the next 6 months as a new guest
		from {country}, on web and on mobile, so each night gets a check every 3 days. The typical position is the middle position of
		the checks of the last 7 days.
		{scan?.window === 'FIRST_SCAN' && ` This first scan (${dayLabel(scan.scanDate)}) checked each night ${scan.checksPerNight} times on web`
			+ `${scan.everyNightUntil !== null ? `: every night to ${dayMonthLabel(scan.everyNightUntil)}, then every 3rd night` : ''}.`}
	</p>;
};

const MethodNote: React.FunctionComponent<{ state: BookingPositionState }> = ({ state }) => {
	const scan = state.scan;
	const country = state.countries[0]?.name ?? 'the home country';
	return <div className="flex max-w-4xl flex-col gap-2 text-xs leading-5 text-gray-600">
		<p>
			How RoomPulse measures: each day RoomPulse checks every 3rd night of the Scan season months in the next 6 months, and starts
			one night later each day. It makes one check per night, per guest country and per device (web and mobile). {country}, the
			home country of the city, gets each night every 3 days; each extra country that the admin adds gets each night every 9 days.
			A check is one search as a new guest, from home internet connections in that country, for 1 night and 2 adults. It reads the
			first 525 results (about 494 places after sponsored ads and repeats are removed), then the rest of the list up to result 1,025.
			A check that does not find a hotel in the first 525 results counts as not in the top 500. The page shows the checks of the last 7 days.
		</p>
		{scan?.window === 'FIRST_SCAN' && <p>
			This first scan ({dayLabel(scan.scanDate)}) was web only, from {country}: {scan.checksPerNight} checks of every night
			{scan.everyNightUntil !== null ? ` to ${dayLabel(scan.everyNightUntil)}, then of every 3rd night` : ''} ({nightsWord(state.nights.length)}).
			The price in the city comes from the price chart that Booking.com shows with each search (&quot;Your budget&quot;). The chart leaves
			out some properties, most of them the dearest; RoomPulse counts them as dearer than you. Sold out comes from RoomPulse&apos;s own
			Booking.com price checks.
		</p>}
	</div>;
};

const Frame: React.FunctionComponent<React.PropsWithChildren> = ({ children }) =>
	<main className="bg-gray-50">
		<div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
			<div className="flex flex-col gap-y-5">{children}</div>
		</div>
	</main>;

// The Booking ranking page: the simple view (chart + compset table) and the detailed view (night grid,
// month table, compset details, scatter). Both views read one query: findBookingPositionStateForPeriod.
const BookingRankingPage: React.FunctionComponent<{ view: View }> = ({ view }) => {
	const { companyHotel } = useSelectedCompanyHotel();
	const { search } = useLocation();
	const form = useForm<BookingRankingFilterValues>({ defaultValues: { channel: DataProvider.BOOKING, country: 'GR', device: 'WEB' } });
	const country = form.watch('country');
	const device = form.watch('device');
	const input = {
		companyHotelId: companyHotel.id,
		channel: DataProvider.BOOKING,
		country,
		device: device === 'MOBILE' ? DataPlatform.MOBILE as const : DataPlatform.WEB as const,
	};
	const query = useQuery({
		queryKey: BookingPositionQueryKeys.state(input),
		queryFn: () => BookingPositionService.findStateForPeriod(input),
		placeholderData: (previous) => previous,
	});

	if (query.isLoading) {
		return <Frame><div className="rounded-lg bg-white px-5 py-16 shadow"><Loader /></div></Frame>;
	}
	if (query.isError || query.data === undefined) {
		return <Frame>
			<Message state="error" title="The Booking ranking could not be loaded.">Please try again later or contact support.</Message>
		</Frame>;
	}
	const state = query.data;
	if (!state.bookingCity.scanActive) {
		return <Frame>
			<Message state="info" title="Booking ranking is not active for this hotel.">
				RoomPulse does not check the Booking city {state.bookingCity.name} yet. An admin activates a Booking city for daily checks.
			</Message>
		</Frame>;
	}

	const right = view === 'simple'
		? <FormButton state="white" isLink to={withSearch(BOOKING_RANKING_DETAIL_PATH, search)}>
			<span className="inline-flex items-center gap-1.5">Detailed view<ChevronRightIcon className="h-4 w-4 text-slate-500" /></span>
		</FormButton>
		: <FormButton state="text" isLink to={withSearch(BOOKING_RANKING_PATH, search)}>
			<span className="inline-flex items-center gap-1"><ChevronLeftIcon className="h-4 w-4" />Simple view</span>
		</FormButton>;
	const mobileHasData = state.devices.some((d) => d.device === 'MOBILE' && d.hasData);
	const isEmpty = state.nights.length === 0 || state.hotels.length === 0;
	const hasAnyData = state.devices.some((d) => d.hasData);
	const insight = insightText(state);

	return <Frame>
		<BookingRankingFilterBar form={form} bookingCity={state.bookingCity} countries={state.countries}
			mobileHasData={mobileHasData} right={hasAnyData ? right : null} />
		{isEmpty && <Message state="info" title={device === 'MOBILE' ? 'First mobile results after the next scan' : 'First results after the first scan'}>
			{device === 'MOBILE'
				? 'RoomPulse checks each night on mobile from the next scan. The page then shows the mobile positions of the last 7 days.'
				: `RoomPulse checks ${state.bookingCity.name} every day. The page shows the positions after the first scan.`}
		</Message>}
		{!isEmpty && view === 'simple' && <>
			<PositionChartCard state={state} />
			{/* Message keeps its own bottom margin (mb-6): the pair sits in one block, so the gap is the margin only */}
			<div>
				{insight !== null && <Message state="info"><span>{insight}</span></Message>}
				<CompsetTable state={state} />
			</div>
			<OtherCityTable state={state} />
			<HowToRead state={state} />
		</>}
		{!isEmpty && view === 'detail' && <>
			<NightGrid state={state} />
			<MonthTable state={state} />
			<CompsetDetailsTable state={state} />
			<PositionPriceScatter state={state} />
			<MethodNote state={state} />
		</>}
	</Frame>;
};

export default BookingRankingPage;
